/**
 * TicketedEventFormRequirement Entity Schemas
 *
 * A form requirement is a soft-FK link from a TicketedEvent to a Form in the
 * forms system (Form.publicId), used to capture attendee data at registration
 * or before attendance. Despite the generalized name here, the underlying
 * Prisma model / table is `TicketedEventWaiverRequirement` (retained to avoid
 * a cosmetic table rename — the concept is generalized at the DTO/API layer).
 *
 * `completionTiming` controls when the requirement is enforced:
 *  - `at_registration` (default) blocks order confirmation / application
 *    approval until satisfied.
 *  - `before_attendance` does not block confirmation; the requirement is
 *    recorded as outstanding and surfaced to organizer + door staff.
 */

import { z } from 'zod'

export const TicketedEventFormCompletionTimingSchema = z.enum(['at_registration', 'before_attendance'])
export type TicketedEventFormCompletionTiming = z.infer<typeof TicketedEventFormCompletionTimingSchema>

export const TicketedEventFormRequirementSchema = z.object({
  id: z.string(),
  formPublicId: z.string(),
  label: z.string(),
  required: z.boolean(),
  perAttendee: z.boolean(),
  sortOrder: z.number().int(),
  completionTiming: TicketedEventFormCompletionTimingSchema
})
export type TicketedEventFormRequirement = z.infer<typeof TicketedEventFormRequirementSchema>

export const CreateTicketedEventFormRequirementSchema = z.object({
  formPublicId: z.string().min(1),
  label: z.string().min(1),
  required: z.boolean().optional(),
  perAttendee: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
  completionTiming: TicketedEventFormCompletionTimingSchema.optional()
})
export type CreateTicketedEventFormRequirementInput = z.infer<typeof CreateTicketedEventFormRequirementSchema>

// `formPublicId` is intentionally excluded from the update shape: reassigning
// the linked form bypasses the create-time cross-school ownership check.
export const UpdateTicketedEventFormRequirementSchema = z.object({
  label: z.string().min(1).optional(),
  required: z.boolean().optional(),
  perAttendee: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
  completionTiming: TicketedEventFormCompletionTimingSchema.optional()
})
export type UpdateTicketedEventFormRequirementInput = z.infer<typeof UpdateTicketedEventFormRequirementSchema>
